import { Loader } from '@10xLabs/react-web/components/Loader'
import { useFormikContext } from 'formik'
import { ReactElement, useMemo } from 'react'
import styled from 'styled-components'

import EmptyState from 'components/v2/EmptyState'
import { IPlaceCoordinatesForm } from 'features/Places/types'
import { isValidLatitude, isValidLongitude } from 'features/Places/utils'
import { device } from 'utils/style'
import { useGoogleMapsScript } from 'hooks/google-maps'
import { googleMapsConfig } from 'config/google-maps'
import { AdvancedMarker, Map } from '@vis.gl/react-google-maps'
import { PinIcon } from 'components/Icons/PinIcon'

const Container = styled.div`
  height: 100%;
  border-radius: 16px;
  height: 400px;
  overflow: hidden;

  @media ${device.laptop} {
    height: 100%;
  }
`

export const PlaceMap = (): ReactElement => {
  const { values, setFieldValue } = useFormikContext<IPlaceCoordinatesForm>()

  const { isLoaded } = useGoogleMapsScript()

  const center = useMemo(() => {
    if (isValidLatitude(values.coordinates.latitude) && isValidLongitude(values.coordinates.longitude)) {
      return { lat: parseFloat(values.coordinates.latitude), lng: parseFloat(values.coordinates.longitude) }
    }
    return { lat: googleMapsConfig.defaultCenter.LAT, lng: googleMapsConfig.defaultCenter.LNG }
  }, [values.coordinates.latitude, values.coordinates.longitude])

  if (!isLoaded) {
    return (
      <EmptyState.Container>
        <Loader />
      </EmptyState.Container>
    )
  }

  const handleDragEnd = async (event: google.maps.MapMouseEvent): Promise<void> => {
    const newLatitude = event.latLng?.lat()
    const newLongitude = event.latLng?.lng()

    if (newLatitude && newLongitude) {
      await setFieldValue('coordinates.latitude', newLatitude.toString())
      setFieldValue('coordinates.longitude', newLongitude.toString())
    }
  }

  return (
    <Container>
      <Map
        mapId="place-map"
        defaultZoom={googleMapsConfig.defaultZoom}
        defaultCenter={center}
        {...googleMapsConfig.defaultOptions}
      >
        <AdvancedMarker position={center} draggable onDragEnd={handleDragEnd}>
          <PinIcon />
        </AdvancedMarker>
      </Map>
    </Container>
  )
}
